/**
 * Licensed Materials - Property of IBM
 *
 * "Restricted Materials of IBM"
 *
 * 5746-SM2
 *
 * (C) Copyright IBM Corp. 2022
 *
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
], function() {

	return {

		/**
		 * check if the sources in the spec have authoring capability
		 * @param {object} dashboardApi - dashboardAPI
		 * @param {object} spec - dashoard or fragment spec
		 * @param {object} spec.dataSources - dataSources property of the spec
		 * @param {object[]} spec.dataSources.sources - array of sources
		 * @param {string} spec.dataSources.sources[i].assetId - asset id of the source element
		 * @param {string} spec.dataSources.sources[i].type - asset type of the source element
		 * @param {string} capabilityErrorMsg - error to show in the toast for deny capability result
		 * @returns {boolean} true if all sources in the spec have authoring capability, false otherwise; and reject for unexpected errors
		 */
		checkAuthoringCapability(dashboardApi, spec, capabilityErrorMsg) {
			const capabilitiesFeature = dashboardApi.getFeature('Capabilities');
			if (!capabilitiesFeature) {
				return Promise.resolve(true);
			}
			return capabilitiesFeature.checkAuthoringCapability({ spec })
				.then((hasCapability) => {
					if (!hasCapability) {
						dashboardApi.showToast(capabilityErrorMsg, {
							type: 'error',
							preventDuplicates: true
						});
					}
					return hasCapability;
				});
		},

		hasCapabilityToSaveDashboard(context) {
			const dashboardView = context.glassContext.appController.currentAppView.currentContentView;
			if (dashboardView.isAuthoringMode) {
				return Promise.resolve(true);
			}
			const stringResources = dashboardView.services.getSvcSync('.StringResources');
			return this.checkAuthoringCapability(dashboardView.getDashboardApi(), dashboardView.boardSpec, stringResources.get('noCapabilityForSave'))
				.catch((err) => {
					context.glassContext.getCoreSvc('.Logger').error('Fail to check source capability in Save', err);
					return false;
				});
		},

		/**
		 * Check if user has global authoring capability in the current view (which could be dashboard/explore/story view)
		 * @param {object} dashboardApi - to get Capabilities and UserProfile features (pass dashboardApi inside an object to be able to change to GlassContext if necessary without changing the signature)
		 * @returns {boolean} return true in 2 cases:
		 * - The current application doesn't have capabilities feature, so do not need to check capability (like PA or DaaS)
		 * - The current application has capability feature and user has the global authoring capability of this current view
		 */
		hasGlobalAuthoringCapability({ dashboardApi }) {
			const capabilitiesFeature = dashboardApi.getFeature('Capabilities');
			const userProfileFeature = dashboardApi.getFeature('UserProfile');
			return !capabilitiesFeature || userProfileFeature.hasCapability(capabilitiesFeature.getCurrentAuthoringCapability());
		},

		/**
		 * @param {object} spec - the live widget spec format defined in LiveWidgetService
		 *
		 * @returns {object[]} array of data source objects that contain source assetId & type
		 */
		extractSourcesFromSpec(spec) {
			const dataViews = spec.data && spec.data.dataViews;
			if (!dataViews || !dataViews.length) {
				throw new Error('Invalid live widget spec');
			}
			const sourceInfos = [];
			dataViews.forEach(dataView => {
				if (dataView.model) {
					sourceInfos.push({
						assetId: dataView.model.assetId,
						type: dataView.model.type
					});
				}
			});
			return sourceInfos;
		}
	};

});
